import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import withStyles from 'react-jss';
import Truncate from 'react-truncate';
import { Translation } from 'react-i18next';
import { debounce } from 'lodash';
import {
  setUser,
  setAuthStatus,
  getTopMatches,
  getSchools,
  setLoading,
  checkUser,
  setRegisterOptions,
  setRogueTenantSSO,
} from '../../store/actions';
import {
  getAuthState,
  getPreRegistrationState,
  getUserScore,
  getTenantSSOFlag,
  getEducationLevelSelector,
  getUserTerritory,
  getRegisterOptionsValue,
  getRogueTenantSSO,
  getProviderName,
} from '../../store/selectors';
import RegisterForm from './RegisterForm';
import Loading from '../utility/Loading';
import { getCategoryImages } from '../utility/getCategoryImages';
import Scrollbar from '../Scrollbar';
import { education } from '../../copy/onboardingCopy.json';
import { isRogueTenantSSO } from './authUtil';
import { getTenantNameFromURL } from '../utility/routeUtil';

import photography from '../../ASUPhotos/stubs/careers/photography.png';
import makeup from '../../ASUPhotos/stubs/careers/makeup.png';
import setDesign from '../../ASUPhotos/stubs/careers/setDesign.png';
import RegisterOptions from '../registration/RegisterOptions';
import SunIcon from '../utility/SunIcon.jsx';
import ProgressBar from '../utility/ProgressBar';
import Fade from '../utility/Fade';
import WindowTitle from '../utility/WindowTitle';

const continueButtonStyles = {
  color: (props) => ({
    backgroundColor: props.tenant ? props.tenant.primary : 'white',
    borderColor: props.tenant ? props.tenant.primary : 'black',

    '&:hover': {
      backgroundColor: 'white',
      border: props.tenant
        ? `2px solid ${props.tenant.primary}`
        : '2px solid black',
      color: props.tenant ? props.tenant.primary : 'black',
      borderStyle: 'solid',
    },
  }),
  blurTopThree: () => ({
    filter: 'blur(4px)',
  }),
};

class Register extends React.Component {
  // Prevent setState on unmounted components
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      width: 0,
      // awful name, but it's standard to react-transition-group
      inProp: true,
      careers: [
        { categoryImageVariant: photography },
        { categoryImageVariant: makeup },
        { categoryImageVariant: setDesign },
      ],
    };
  }

  primaryColor = {
    color: this.props.tenant.primary,
  };

  primaryBackground = {
    backgroundColor: this.props.tenant.primary,
  };

  secondaryBorderBottom = {
    borderBottom: `5px solid ${this.props.tenant.secondary}`,
  };

  get onboarding() {
    return this.props.onboarding;
  }

  get preregistration() {
    return this.props.preregistration;
  }

  get rogueTenantSSO() {
    return this.props.rogueTenantSSO;
  }

  get registerOptions() {
    return this.preregistration.registerOptionsValue;
  }

  get userScore() {
    return this.props.userScore;
  }

  get tenantHasSSO() {
    return this.props.tenantSSOFlag;
  }

  get auth() {
    return this.props.auth;
  }

  get isAuthenticated() {
    return this.auth.isAuthenticated;
  }

  get userTerritory() {
    return this.props.userTerritory;
  }

  get userEducation() {
    return this.props.userEducationLevel;
  }

  get registerOptionsValue() {
    return this.props.registerOptionsValue;
  }

  get providerName() {
    return this.props.providerName;
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.preregistration?.topThreeMatches !==
        prevProps.preregistration?.topThreeMatches &&
      this.props.preregistration?.topThreeMatches[0]?.categoryImageUrl1 &&
      this.props.preregistration?.topThreeMatches[0]?.categoryImageUrl1 !==
        'testUrl' &&
      this._isMounted
    ) {
      // this is okay when using prevProps
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        careers: getCategoryImages(this.props.preregistration.topThreeMatches),
      });
    }
  }

  async componentDidMount() {
    window.addEventListener('resize', this.onResize);
    this.onResize();

    this._isMounted = true;

    await this.props.getTopMatches(this.userScore);

    if (this.userTerritory && this.userEducation === education.degree2) {
      this.props.getSchools(this.userTerritory);
    }

    // For handling multi-tenant users who switch url
    if (this.isAuthenticated && this.registerOptionsValue) {
      if (this.registerOptionsValue === 'federation') {
        const verifyRogueTenantSSO = this.checkIsRogueTenantSSO();
        this.props.setRogueTenantSSO(!!verifyRogueTenantSSO);
      }

      this.props.setRegisterOptions(this.registerOptionsValue);
    }

    window.addEventListener('unload', (e) => {
      e.preventDefault();
      window.scrollTo(0, 0);
    });
  }

  componentWillUnmount() {
    if (this._isMounted) {
      this.setState({ inProp: false });
    }

    window.scrollTo(0, 0);
    this._isMounted = false;
    window.removeEventListener('resize', this.onResize);
  }

  onResize = debounce(() => {
    if (this._isMounted) {
      this.setState({ width: window.innerWidth });
    }
  }, 75);

  setInPropInParent = () => {
    if (this._isMounted) {
      this.setState({ inProp: false });
    }
  };

  checkIsRogueTenantSSO = () => {
    const { tenantName } = { ...getTenantNameFromURL() };
    return this.providerName && isRogueTenantSSO(tenantName, this.providerName);
  };

  getRegisterOptions = () => {
    return this.registerOptionsValue && !this.rogueTenantSSO ? (
      <RegisterForm
        color={this.props.classes.color}
        setInPropInParent={this.setInPropInParent}
      />
    ) : (
      <RegisterOptions tenantHasSSO={this.tenantHasSSO} />
    );
  };

  render() {
    const { tenant } = this.props;

    if (this.props.preregistration.topThreeMatches[0] === undefined) {
      return <Loading height="100%" />;
    }

    const { careers } = this.state;

    const blurTopThreeCareers =
      tenant.name === 'Nevada' ? this.props.classes.blurTopThree : null;

    return (
      <Translation>
        {(t) => (
          <>
            <WindowTitle>{t('Register')}</WindowTitle>
            <Fade in={this.state.inProp}>
              <div className="user-content-register">
                <div className="main-left-register">
                  <div className="header-container-register bottom-border">
                    <div className="register-text-container">
                      <h1 className="register-header-text left left-header">
                        {t('Explore your potential careers')}
                      </h1>
                      <p className="subheading">
                        {t('Here are three of your top options')}
                      </p>
                      <div
                        className="yellow-border-bar"
                        style={this.secondaryBorderBottom}
                      />
                    </div>
                  </div>
                  <div
                    className={[
                      'main-left-content',
                      'bottom-border-grey',
                      blurTopThreeCareers,
                    ].join(' ')}
                  >
                    <div className="career-picture-container">
                      <img
                        alt="Career"
                        className="career-picture-register"
                        src={careers[0].categoryImageVariant}
                      />

                      <div
                        className="career-content register"
                        style={this.secondaryBorderBottom}
                      >
                        <p className="career-title-register">
                          <Truncate lines={2} ellipsis="...">
                            {
                              this.props.preregistration.topThreeMatches[0]
                                .titleEn
                            }
                          </Truncate>
                        </p>
                        {careers[0].careerOutlook && (
                          <SunIcon
                            setParentOnClick={this.props.setBrightOutlookModal}
                            additionalClassName={
                              tenant.name === 'Nevada'
                                ? 'register-no-pointer'
                                : 'register-pointer'
                            }
                          />
                        )}
                      </div>
                    </div>
                    <div className="career-picture-container">
                      <img
                        className="career-picture-register"
                        alt="Career"
                        src={careers[1].categoryImageVariant}
                      />

                      <div
                        className="career-content register"
                        style={this.secondaryBorderBottom}
                      >
                        <p className="career-title-register">
                          <Truncate lines={2} ellipsis="...">
                            {
                              this.props.preregistration.topThreeMatches[1]
                                .titleEn
                            }
                          </Truncate>
                        </p>
                        {careers[1].careerOutlook && (
                          <SunIcon
                            setParentOnClick={this.props.setBrightOutlookModal}
                            additionalClassName={
                              tenant.name === 'Nevada'
                                ? 'register-no-pointer'
                                : 'register-pointer'
                            }
                          />
                        )}
                      </div>
                    </div>
                    <div className="career-picture-container">
                      <img
                        alt="Career"
                        className="career-picture-register"
                        src={careers[2].categoryImageVariant}
                      />
                      <div
                        className="career-content register"
                        style={this.secondaryBorderBottom}
                      >
                        <p className="career-title-register invisible-focus-indicator">
                          <Truncate lines={2} ellipsis="...">
                            {
                              this.props.preregistration.topThreeMatches[2]
                                .titleEn
                            }
                          </Truncate>
                        </p>
                        {careers[2].careerOutlook && (
                          <SunIcon
                            setParentOnClick={this.props.setBrightOutlookModal}
                            additionalClassName={
                              tenant.name === 'Nevada'
                                ? 'register-no-pointer'
                                : 'register-pointer'
                            }
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="main-right-register">
                  <div className="header-container-register bottom-border">
                    <h1 className="register-header-text right right-header-2">
                      {t('Start building your career journey')}
                    </h1>
                    {/* Conditionally render different text in mobile */}
                    <h1 className="register-header-text-mobile right-header-3">
                      {t('Start building your career journey')}
                    </h1>
                    <p className="subheading right">
                      {t(
                        'Create a free account to see all 25 careers, educational opportunities, career outlooks and more!'
                      )}
                    </p>
                    <div
                      className="yellow-border-bar right"
                      style={this.secondaryBorderBottom}
                    />
                  </div>
                  {this.state.width >= 1024 ? (
                    <Scrollbar>{this.getRegisterOptions()}</Scrollbar>
                  ) : (
                    this.getRegisterOptions()
                  )}
                </div>
              </div>
              <div className="register-progress-bars">
                <ProgressBar
                  bars={this.props.bars}
                  tenant={this.props.tenant}
                />
              </div>
            </Fade>
          </>
        )}
      </Translation>
    );
  }
}

const mapStateToProps = (state) => {
  const preregistration = getPreRegistrationState(state);
  const auth = getAuthState(state);
  const tenant = state.tenant;
  const rogueTenantSSO = getRogueTenantSSO(state);
  const userScore = getUserScore(state);
  const tenantSSOFlag = getTenantSSOFlag(state);
  const userEducationLevel = getEducationLevelSelector(state);
  const userTerritory = getUserTerritory(state);
  const registerOptionsValue = getRegisterOptionsValue(state);
  const providerName = getProviderName(state);

  return {
    auth,
    preregistration,
    tenant,
    rogueTenantSSO,
    userScore,
    tenantSSOFlag,
    userEducationLevel,
    userTerritory,
    registerOptionsValue,
    providerName,
  };
};

const RegisterWithRouter = withRouter(
  connect(mapStateToProps, {
    setAuthStatus,
    setUser,
    getTopMatches,
    getSchools,
    setLoading,
    checkUser,
    setRegisterOptions,
    setRogueTenantSSO,
  })(withStyles(continueButtonStyles)(Register))
);

export default RegisterWithRouter;
